import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatMinSecTenth } from "./operations";
import { checkRange } from "./timeRange";

/**
 * Start and end time boxes for an audio or video range (Audio Trimmer, Extract Audio). Typing is forgiving
 * ("83", "1:23", "1:23.5"). A start/end pair that cannot be used shows a message under the boxes and stays as
 * typed (instead of snapping back and leaving only a toast that is gone in seconds); `onValidChange(false)` lets
 * the tool keep its action button off until the times are right. Moving the slider updates the boxes.
 */
export function TimeRangeFields({ range, duration, onChange, onValidChange }: {
  range: [number, number];
  duration: number;
  onChange: (next: [number, number]) => void;
  onValidChange?: (valid: boolean) => void;
}) {
  const [startText, setStartText] = useState(formatMinSecTenth(range[0]));
  const [endText, setEndText] = useState(formatMinSecTenth(range[1]));
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The slider (or a preset) moved the range: show it in the boxes and clear any old message.
  useEffect(() => {
    setStartText(formatMinSecTenth(range[0]));
    setEndText(formatMinSecTenth(range[1]));
    setError(null);
    onValidChange?.(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range[0], range[1]]);

  const commit = (s: string, e: string) => {
    const r = checkRange(s, e, duration);
    if (!r.ok) { setError(r.message); onValidChange?.(false); return; }
    setError(null);
    onValidChange?.(true);
    if (r.clamped) setEndText(formatMinSecTenth(r.end));
    if (r.start !== range[0] || r.end !== range[1]) onChange([r.start, r.end]);
  };
  // Checked a moment after typing stops (and straight away on Enter or leaving the box), so the message does not flicker mid-word.
  const typed = (s: string, e: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => commit(s, e), 500);
  };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const now = () => { if (timer.current) clearTimeout(timer.current); commit(startText, endText); };
  const keys = (ev: React.KeyboardEvent<HTMLInputElement>) => { if (ev.key === "Enter") now(); };

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-muted-foreground">Start</Label>
          <Input aria-label="Start time" aria-invalid={!!error} value={startText} onChange={(e) => { setStartText(e.target.value); typed(e.target.value, endText); }} onBlur={now} onKeyDown={keys} className="w-20 h-8 text-xs font-mono" placeholder="0:00" />
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-muted-foreground">End</Label>
          <Input aria-label="End time" aria-invalid={!!error} value={endText} onChange={(e) => { setEndText(e.target.value); typed(startText, e.target.value); }} onBlur={now} onKeyDown={keys} className="w-20 h-8 text-xs font-mono" placeholder="0:00" />
        </div>
        <span className="text-xs text-muted-foreground">minutes:seconds. Type an exact time if dragging is not precise enough.</span>
      </div>
      {error && <p role="alert" data-testid="range-error" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
